import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { AssetsService } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import { Dossier } from '@sc/data-access-models';
import {
  DossierSandboxService,
  FittersSandboxService,
} from '@sc/data-access-state-management';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { ScreenUtils } from '@sc/utils';
import { Observable, of } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { NgIf, AsyncPipe } from '@angular/common';

@Component({
  selector: 'sc-insurer-logo',
  styleUrls: ['./insurer-logo.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="logo-container"
      [class.clickable]="getLogoLink()"
      [attr.tabindex]="getLogoLink() ? '0' : undefined"
      [attr.role]="getLogoLink() ? 'link' : undefined"
      (click)="goToWebsite()"
      (keydown.enter)="goToWebsite()"
      (keydown.space)="goToWebsite()"
    >
      <img
        class="insurer-logo main-logo"
        *ngIf="displayMainLogo"
        [src]="displayLogo()"
        (error)="withClientAppId = true"
        [alt]="
          (getLogoAlt('configIdLogo') | async) ||
          configurationService.getConfig()?.insurerName
        "
      />
      <ng-container *ngIf="businessOrigin$ | async as businessOrigin">
        <img
          [class]="'insurer-logo co-logo ' + businessOrigin"
          *ngIf="secondaryLogoFound"
          [src]="displaySecondaryLogo(businessOrigin)"
          (error)="secondaryLogoFound = false"
          [alt]="
            (getLogoAlt('businessOriginCoLogo', businessOrigin) | async) ||
            businessOrigin
          "
        />
      </ng-container>
    </div>
  `,
  imports: [NgIf, AsyncPipe],
})
export class InsurerLogoComponent {
  @Input() configId: string;
  withClientAppId = false;
  secondaryLogoFound = true;
  displayMainLogo = true;
  dossier: Dossier;

  businessOrigin$: Observable<string | undefined>;

  constructor(
    public configurationService: ConfigurationService,
    private readonly featureFlippingManagerService: FeatureFlippingManagerService,
    public assetsService: AssetsService,
    private readonly dossierSB: DossierSandboxService,
    private readonly fitterSB: FittersSandboxService
  ) {
    this.dossierSB?.dossier$
      ?.pipe(
        filter((dossier) => !!dossier),
        take(1)
      )
      .subscribe((newDossier) => {
        this.dossier = newDossier;
        const displayMainLogo =
          this.configurationService?.getOriginFilterFormCreationData(
            this.dossier?.creationData
          )?.options?.displayMainLogo;
        this.displayMainLogo = displayMainLogo !== false;
      });

    this.businessOrigin$ = this.fitterSB.ctaQueryState$.pipe(
      map(
        (queryState) =>
          this.dossier?.creationData?.businessOrigin ||
          queryState?.businessOrigin
      ),
      tap((businessOrigin) => {
        if (businessOrigin) {
          this.secondaryLogoFound = true;
        }
      })
    );
  }

  @HostListener('window:resize', ['$event'])
  displayLogo() {
    return this.assetsService.getConfigLogoPath(
      ScreenUtils.isMobileResolution(),
      this.withClientAppId
    );
  }

  displaySecondaryLogo(businessOrigin: string) {
    return this.assetsService.getSecondaryLogo(businessOrigin);
  }

  getLogoAlt(
    itemType: 'configIdLogo' | 'businessOriginCoLogo',
    businessOrigin?: string
  ) {
    const logoItem =
      itemType === 'configIdLogo'
        ? this.configurationService.getConfig()?.id
        : businessOrigin;

    if (!logoItem) {
      return of('');
    }

    return this.assetsService.getLogoAlt(logoItem, itemType);
  }

  goToWebsite() {
    const url = this.getLogoLink();
    if (url) {
      window.open(url, '_self');
    }
  }

  getLogoLink() {
    if (
      this.featureFlippingManagerService.isFeatureActivated('navbar-logo-link')
    )
      return this.configurationService.getConfig().website;
    return false;
  }
}
